import { add, listen, qs, qsa, remove, toggle } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { createFocusTrap } from "focus-trap";

import { formatMoney } from "@/neon";

const routes = window.theme.routes.cart || {};

const { strings } = window.theme;

const selectors = {
  productVariant: "[data-variant-select]",
  form: "[data-form]",
  country: "[data-address-country]",
  province: "[data-address-province]",
  provinceWrapper: "[data-address-province-wrapper]",
  zip: "[data-address-zip]",
  modal: "[data-estimator-modal]",
  wash: "[data-mobile-wash]",
  trigger: "[data-estimator-trigger]",
  estimateButton: "[data-estimator-button]",
  success: "[data-estimator-success]",
  error: "[data-estimator-error]",
  close: "[data-close-icon]",
};

const classes = {
  active: "active",
  hidden: "hidden",
  visible: "is-visible",
  fixed: "is-fixed",
};

const ShippingEstimator = (node, container) => {
  const form = qs(selectors.form, node);
  const productSelect = qs(selectors.productVariant, container);
  const countrySelector = qs(selectors.country, node);
  const provinceSelector = qs(selectors.province, node);
  const provinceWrapper = qs(selectors.provinceWrapper, node);
  const zipInput = qs(selectors.zip, node);
  const modal = qs(selectors.modal, node);
  const wash = qs(selectors.wash, node);
  const trigger = qs(selectors.trigger, node);
  const estimate = qs(selectors.estimateButton, node);
  const successMessage = qs(selectors.success, node);
  const errorMessage = qs(selectors.error, node);
  let focusTrap = null;
  let cartCookie;

  // Add dummy placeholder option
  const firstCountryOptions = qsa("option", countrySelector);
  if (firstCountryOptions.length > 1) {
    firstCountryOptions[0].setAttribute("selected", true);
    firstCountryOptions[0].innerText =
      strings.products.product.country_placeholder;
  }

  _checkForProvince();

  const events = [
    listen(form, "submit", e => {
      e.preventDefault();

      _estimateShipping();
    }),
    listen(countrySelector, "change", _checkForProvince),
    listen(trigger, "click", _open),
    listen(wash, "click", _close),
    listen(qs(selectors.close, node), "click", _close),
    listen(estimate, "click", _estimateShipping),
    listen(modal, "keydown", ({ keyCode }) => {
      if (keyCode === 27) _close();
    }),
  ];

  /* get cookie by name */
  const getCookie = name => {
    const value = "; " + document.cookie;
    const parts = value.split("; " + name + "=");
    if (parts.length == 2) return parts.pop().split(";").shift();
  };

  /* update the cart cookie value */
  const updateCartCookie = a => {
    const date = new Date();
    date.setTime(date.getTime() + 14 * 86400000);
    const expires = "; expires=" + date.toGMTString();
    document.cookie = "cart=" + a + expires + "; path=/";
  };

  /* reset the cart cookie value */
  const resetCartCookie = () => {
    updateCartCookie(cartCookie);
  };

  /* get the rates */
  const getRates = variantId => {
    add(estimate, "loading");
    if (typeof variantId === "undefined") return;

    const productQuantity = qs("[data-quantity-input]", container);

    const quantity = productQuantity ? parseInt(productQuantity.value) : 1;

    const addData = {
      id: variantId,
      quantity: quantity,
    };

    fetch(routes.add + ".js", {
      body: JSON.stringify(addData),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "xmlhttprequest",
      },
      method: "POST",
    })
      .then(response => {
        return response.json();
      })
      .then(() => {
        errorMessage.innerHTML = "";
        successMessage.innerHTML = "";
        remove(successMessage, "active");
        const countryQuery = `shipping_address%5Bcountry%5D=${countrySelector.value}`;
        const provinceQuery = `shipping_address%5Bprovince%5D=${provinceSelector.value}`;
        const zipQuery = `shipping_address%5Bzip%5D=${zipInput.value}`;

        const requestUrl = `${routes.shipping}.json?${countryQuery}&${provinceQuery}&${zipQuery}`;

        const request = new XMLHttpRequest();

        request.open("GET", requestUrl, true);

        request.onload = () => {
          const response = JSON.parse(request.response);
          if (request.status >= 200 && request.status < 300) {
            if (response.shipping_rates && response.shipping_rates.length) {
              add(successMessage, "active");
              response.shipping_rates.forEach(rate => {
                const rateElement = `
                  <li class="shipping-estimator-modal__success-item">
                    <h4 class="ma0">${rate.name}</h4>
                    <span>${formatMoney(rate.price)}</span>
                  </li>
                `;
                successMessage.insertAdjacentHTML("beforeend", rateElement);
              });
            } else {
              const noRate = `
                <li class="shipping-estimator-modal__success-item">
                  <span>${strings.products.product.no_shipping_rates}</span>
                </li>
              `;
              successMessage.insertAdjacentHTML("beforeend", noRate);
            }
          } else {
            for (const [key, value] of Object.entries(response)) {
              const errorElement = `
              <li class="shipping-estimator-modal__error-item">
                <div><span>${key}</span> ${value}</div>
              </li>
            `;
              errorMessage.insertAdjacentHTML("beforeend", errorElement);
            }
          }
          resetCartCookie();
          remove(estimate, "loading");
        };
        request.send();
      })
      .catch(() => {
        resetCartCookie();
        remove(estimate, "loading");
      });
  };

  function _checkForProvince() {
    const selected = qs(`[value="${countrySelector.value}"]`, countrySelector);

    const provinces = JSON.parse(selected.dataset.provinces);

    toggle(provinceWrapper, classes.hidden, !provinces.length);

    provinceSelector.innerHTML = provinces.reduce((acc, curr) => {
      return acc + `<option value="${curr[0]}">${curr[0]}</option>`;
    }, "");
  }

  function _estimateShipping() {
    if (!productSelect.value.length) return;

    cartCookie = getCookie("cart");

    let tempCookieValue = "temp-cart-cookie___" + Date.now();
    let fakeCookieValue = "fake-cart-cookie___" + Date.now();

    // If not found, make a new temp cookie
    if (!cartCookie) {
      updateCartCookie(tempCookieValue);
      cartCookie = getCookie("cart");
    }

    // If found but has a weird length, abort
    if (cartCookie.length < 32) return;

    /* Change the cookie value to a new 32 character value */
    updateCartCookie(fakeCookieValue);

    getRates(parseInt(productSelect.value));
  }

  function _open(e) {
    e.preventDefault();

    add(modal, classes.fixed);

    setTimeout(() => {
      add(modal, classes.visible, classes.active);
    }, 50);

    modal.setAttribute("aria-hidden", "false");

    focusTrap = createFocusTrap(modal, { allowOutsideClick: true });
    focusTrap.activate();
    disableBodyScroll(modal, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  }

  function _close(e) {
    e && e.preventDefault();
    remove(modal, classes.visible, classes.active);

    focusTrap && focusTrap.deactivate();

    setTimeout(() => {
      remove(modal, classes.fixed);
    }, 300);

    modal.setAttribute("aria-hidden", "true");
    enableBodyScroll(modal);
  }

  return () => {
    events.forEach(unsubscribe => unsubscribe());
  };
};

export default ShippingEstimator;
